import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../BottomNav';
import theme from '../theme';

function Section({ title, emoji, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ border: `1px solid ${theme.colors.border}`, borderRadius: theme.radius.lg, marginBottom: theme.spacing.lg, overflow: 'hidden' }}>
      <div
        onClick={() => setOpen(!open)}
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', cursor: 'pointer', background: open ? '#f0faf0' : theme.colors.white }}>
        <span style={{ fontWeight: 600, fontSize: theme.font.size.lg }}>{emoji} {title}</span>
        <span style={{ color: theme.colors.textMuted, fontSize: 18 }}>{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <div style={{ padding: '0 16px 16px', fontSize: theme.font.size.md, color: '#444', lineHeight: 1.7 }}>
          {children}
        </div>
      )}
    </div>
  );
}

function Guide() {
  const navigate = useNavigate();

  return (
    <div style={{ maxWidth: theme.layout.maxWidth, margin: '0 auto', fontFamily: theme.font.family }}>
      <div style={{ background: theme.colors.primary, padding: '16px 20px', display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
        <button onClick={() => navigate('/profilo')} style={{
          background: 'none', border: 'none', color: theme.colors.white,
          fontSize: 22, cursor: 'pointer', padding: 0
        }}>←</button>
        <h2 style={{ margin: 0, color: theme.colors.white, fontSize: theme.font.size.xxl }}>📖 Come funziona</h2>
      </div>

      <div style={{ padding: theme.spacing.xl, paddingBottom: 80 }}>

        <div style={{ background: theme.colors.primaryLight, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginBottom: theme.spacing.xl, fontSize: theme.font.size.md, color: theme.colors.primary, lineHeight: 1.6 }}>
          <strong>Jobbetty</strong> è pensato per lavoretti occasionali tra privati — ripetizioni, babysitting, giardinaggio e altro — senza partita IVA, nel rispetto della legge italiana.
        </div>

        <Section emoji="📋" title="Cos'è una prestazione occasionale?">
          <p>Una <strong>prestazione occasionale</strong> è un'attività lavorativa svolta in modo saltuario, non continuativo, da una persona fisica verso un'altra persona fisica o un'azienda.</p>
          <p>È regolata dall'<strong>art. 2222 del Codice Civile</strong> e non richiede la partita IVA se i compensi annui rimangono entro i limiti di legge.</p>
          <p>Esempi tipici: ripetizioni scolastiche, babysitting, piccoli lavori di giardinaggio, lavaggio auto, traslochi leggeri.</p>
        </Section>

        <Section emoji="💶" title="Limiti di reddito: i €5.000">
          <p>Per restare nel regime di prestazione occasionale senza aprire partita IVA, devi rispettare questi limiti:</p>
          <div style={{ background: theme.colors.warningBg, borderRadius: theme.radius.sm, padding: 12, margin: '10px 0' }}>
            <div style={{ fontWeight: 700, color: theme.colors.warning, fontSize: theme.font.size.lg, marginBottom: 6 }}>⚠️ Limiti annuali</div>
            <div style={{ marginBottom: 4 }}>• <strong>€5.000</strong> per anno solare da ogni singolo committente</div>
            <div>• <strong>€5.000</strong> totali da tutti i committenti messi insieme</div>
          </div>
          <p>Se superi questi limiti, sei obbligato ad aprire la partita IVA. Jobbetty ti avvisa automaticamente quando ti avvicini alla soglia nella sezione "I miei guadagni".</p>
        </Section>

        <Section emoji="🧾" title="Tasse e contributi">
          <p>I compensi da prestazione occasionale sono soggetti a:</p>
          <div style={{ background: theme.colors.background, borderRadius: theme.radius.sm, padding: 12, margin: '10px 0' }}>
            <div style={{ marginBottom: 8 }}>
              <strong>IRPEF (ritenuta d'acconto 20%)</strong><br/>
              <span style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Se il committente è un'azienda o un professionista, deve trattenere il 20% del compenso e versarlo allo Stato. Se il committente è un privato, non c'è obbligo di ritenuta.</span>
            </div>
            <div style={{ marginBottom: 8 }}>
              <strong>Dichiarazione dei redditi</strong><br/>
              <span style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>I compensi vanno sempre dichiarati nel modello 730 o Redditi PF, nel quadro dedicato ai redditi diversi.</span>
            </div>
            <div>
              <strong>Contributi INPS</strong><br/>
              <span style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Se superi i €5.000 annui complessivi, scatta l'obbligo di iscrizione alla gestione separata INPS con versamento dei contributi previdenziali.</span>
            </div>
          </div>
          <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.sm }}>ℹ️ Jobbetty non è un consulente fiscale. Per situazioni complesse, consulta un commercialista.</p>
        </Section>

        <Section emoji="📄" title="La ricevuta: quando e come farla">
          <p>Quando ricevi un pagamento per una prestazione occasionale, <strong>devi sempre rilasciare una ricevuta</strong> al committente.</p>
          <div style={{ background: theme.colors.background, borderRadius: theme.radius.sm, padding: 12, margin: '10px 0' }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>La ricevuta deve contenere:</div>
            <div style={{ marginBottom: 4 }}>✓ I tuoi dati (nome, cognome, codice fiscale, indirizzo)</div>
            <div style={{ marginBottom: 4 }}>✓ I dati del committente</div>
            <div style={{ marginBottom: 4 }}>✓ Descrizione del lavoro svolto</div>
            <div style={{ marginBottom: 4 }}>✓ Importo del compenso</div>
            <div style={{ marginBottom: 4 }}>✓ Data della prestazione</div>
            <div>✓ Dicitura: <em>"Compenso per prestazione occasionale ai sensi dell'art. 2222 C.C. — non soggetto a IVA"</em></div>
          </div>
          <div style={{ background: theme.colors.primaryLight, borderRadius: theme.radius.sm, padding: 12, marginTop: 10 }}>
            <strong style={{ color: theme.colors.primary }}>💡 Jobbetty genera la ricevuta per te!</strong>
            <p style={{ margin: '6px 0 0', fontSize: theme.font.size.base }}>Vai su "I miei guadagni" → seleziona un lavoro completato → clicca "Genera ricevuta PDF". Tutto compilato automaticamente.</p>
          </div>
          <p style={{ color: theme.colors.textMuted, fontSize: theme.font.size.sm, marginTop: 10 }}>⚠️ Se il compenso supera €77,47, è obbligatorio applicare una <strong>marca da bollo da €2</strong> sulla ricevuta originale cartacea.</p>
        </Section>

        <Section emoji="🤝" title="Come funziona Jobbetty">
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>1. Pubblica un annuncio</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Descrivi il lavoro, imposta il compenso e pubblica. La tua posizione GPS viene rilevata automaticamente per mostrare l'annuncio agli utenti vicini a te.</div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>2. Ricevi candidature</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Gli utenti interessati si candidano con un messaggio. Puoi accettare o rifiutare ogni candidatura dal tuo profilo.</div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>3. Chatta e accordati</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Quando accetti una candidatura si apre automaticamente una chat privata per organizzare i dettagli del lavoro.</div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>4. Registra il guadagno</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Dopo aver completato il lavoro, registra il pagamento in "I miei guadagni" e genera la ricevuta PDF in un click.</div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>5. Resta nei limiti</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Jobbetty monitora i tuoi guadagni annuali e ti avvisa quando ti avvicini alle soglie di legge (€3.500, €4.500, €5.000).</div>
          </div>
        </Section>

        <Section emoji="❓" title="Domande frequenti">
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Posso lavorare per più persone contemporaneamente?</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Sì, ma il limite di €5.000 si applica al totale dei compensi ricevuti da tutti i committenti nell'anno solare.</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Ho 16 anni, posso usare Jobbetty?</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>I minori di 18 anni possono svolgere prestazioni occasionali ma potrebbero necessitare del consenso dei genitori. Verifica con un adulto.</div>
          </div>
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Cosa succede se supero i €5.000?</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Devi aprire la partita IVA. Jobbetty ti avvisa in anticipo così hai tempo per organizzarti. Consulta un commercialista per i passi successivi.</div>
          </div>
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>La ricevuta generata da Jobbetty è valida legalmente?</div>
            <div style={{ color: theme.colors.textSecondary, fontSize: theme.font.size.base }}>Sì, contiene tutti gli elementi richiesti dalla normativa italiana. Ricorda di applicare la marca da bollo da €2 se il compenso supera €77,47.</div>
          </div>
        </Section>

        <div style={{ background: theme.colors.warningBg, borderRadius: theme.radius.lg, padding: theme.spacing.lg, marginTop: 8, fontSize: theme.font.size.base, color: theme.colors.warning, lineHeight: 1.6 }}>
          <strong>⚠️ Disclaimer</strong><br/>
          Le informazioni presenti in questa pagina hanno scopo puramente informativo e non costituiscono consulenza fiscale o legale. La normativa può variare — per situazioni specifiche consulta sempre un commercialista o un consulente del lavoro.
        </div>

      </div>
      <BottomNav />
    </div>
  );
}

export default Guide;